/**
 * Explore page - Enhanced character browsing
 * With search, filters, and character cards
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

function Explore() {
  const { language, user } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCategories();
    loadCharacters();
  }, [selectedCategory, sortBy, genderFilter]);

  const loadCategories = async () => {
    try {
      const response = await axios.get('/explore/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    }
  };

  const loadCharacters = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        sort: sortBy,
        limit: 50
      };
      if (selectedCategory) {
        params.category = selectedCategory;
      }
      if (genderFilter && genderFilter !== 'all') {
        params.gender = genderFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const response = await axios.get('/explore/characters', { params });
      setCharacters(response.data.characters || []);
    } catch (error) {
      console.error('Failed to load characters:', error);
      setError(language === 'en' ? 'Failed to load characters. Please try again later.' : 'Не удалось загрузить персонажей. Попробуйте позже.');
      setCharacters([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadCharacters();
  };

  const translations = {
    en: {
      title: "Find Your Girl",
      titleAll: "Find Your Match",
      subtitle: "or create your own character",
      searchPlaceholder: "Search characters...",
      all: "All",
      women: "Women",
      men: "Men",
      sortPopular: "Popular",
      sortNewest: "Newest",
      sortName: "Name",
      likes: "likes",
      comments: "comments",
      chat: "Chat",
      noResults: "No characters found"
    },
    ru: {
      title: "Найди Свою Девушку",
      titleAll: "Найди своего персонажа",
      subtitle: "или создай своего персонажа",
      searchPlaceholder: "Поиск персонажей...",
      all: "Все",
      women: "Женщины",
      men: "Мужчины",
      sortPopular: "Популярные",
      sortNewest: "Новые",
      sortName: "По имени",
      likes: "лайков",
      comments: "комментариев",
      chat: "Чат",
      noResults: "Персонажи не найдены"
    }
  };

  const t = translations[language] || translations.en;

  const commonCategories = [
    "All", "MILF", "Teen", "Asian", "Latina", "Blonde", "Busty",
    "Submissive", "Dominant", "BDSM", "Romantic", "Athletic"
  ];

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Header Section with background image */}
      <div 
        className="text-white relative" 
        style={{
          backgroundImage: 'url(/static/backgroundFYG.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage: `
            linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 8%, rgba(0,0,0,0.7) 15%, rgba(0,0,0,1) 25%, rgba(0,0,0,1) 75%, rgba(0,0,0,1) 85%, rgba(0,0,0,0.7) 92%, rgba(0,0,0,0.3) 98%, transparent 100%),
            linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 8%, rgba(0,0,0,0.7) 15%, rgba(0,0,0,1) 25%, rgba(0,0,0,1) 75%, rgba(0,0,0,1) 85%, rgba(0,0,0,0.7) 92%, rgba(0,0,0,0.3) 98%, transparent 100%)
          `,
          maskImage: `
            linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 8%, rgba(0,0,0,0.7) 15%, rgba(0,0,0,1) 25%, rgba(0,0,0,1) 75%, rgba(0,0,0,1) 85%, rgba(0,0,0,0.7) 92%, rgba(0,0,0,0.3) 98%, transparent 100%),
            linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 8%, rgba(0,0,0,0.7) 15%, rgba(0,0,0,1) 25%, rgba(0,0,0,1) 75%, rgba(0,0,0,1) 85%, rgba(0,0,0,0.7) 92%, rgba(0,0,0,0.3) 98%, transparent 100%)
          `,
          WebkitMaskComposite: 'multiply',
          maskComposite: 'intersect'
        }}
      >
        {/* Header */}
        <div className="text-center py-8 px-4">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-2">
            {(() => {
              const fullTitle = genderFilter === 'all' ? t.titleAll : t.title;
              const words = fullTitle.split(' ');
              const firstPart = words.slice(0, -1).join(' ');
              const lastWord = words.slice(-1)[0] || '';
              return (
                <>
                  <span className="text-white">{firstPart}</span>{' '}
                  <span className="text-burgundy-500">{lastWord}</span>
                </>
              );
            })()}
          </h1>
          <p className="text-gray-400 text-lg">
            {t.subtitle}
          </p>
        </div>

        {/* Gender tabs */}
        <div className="max-w-7xl mx-auto px-4 flex justify-center gap-2 mb-4">
          {(['all', 'female', 'male']).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGenderFilter(g)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                genderFilter === g
                  ? 'bg-burgundy-600 text-white'
                  : 'bg-dark-800 text-gray-400 hover:bg-dark-700 hover:text-white'
              }`}
            >
              {g === 'all' ? t.all : g === 'female' ? t.women : t.men}
            </button>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="max-w-7xl mx-auto px-4 pb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 mb-4">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full px-4 py-3 pl-10 bg-dark-800 border border-burgundy-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500"
            />
            <svg
              className="absolute left-3 top-3.5 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 bg-dark-800 border border-burgundy-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500"
          >
            <option value="popular">{t.sortPopular}</option>
            <option value="newest">{t.sortNewest}</option>
            <option value="name">{t.sortName}</option>
          </select>
        </form>

        {/* Category Tabs - Removed as tags don't work */}
        </div>
      </div>

      {/* Characters Grid Section with different background */}
      <div>
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 pb-8 pt-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-400">{language === 'en' ? 'Loading characters...' : 'Загрузка персонажей...'}</div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="bg-red-900 text-red-200 rounded-lg p-4 max-w-md mx-auto mb-4">
              {error}
            </div>
            <button
              onClick={() => {
                setError(null);
                loadCharacters();
              }}
              className="px-4 py-2 bg-burgundy-600 hover:bg-burgundy-700 text-white rounded-md"
            >
              {language === 'en' ? 'Retry' : 'Повторить'}
            </button>
          </div>
        ) : characters.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400">{t.noResults}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-7 xl:gap-8">
            {characters.map((character) => {
              const description = language === 'en' 
                ? (character.description_en || character.description || '')
                : (character.description || character.description_en || '');
              
              const handleCharacterClick = (e) => {
                e.preventDefault();
                // Allow unregistered users to view chat (but they can't send messages)
                navigate(`/web/chat/${character.id}`);
              };

              return (
                <div
                  key={character.id}
                  onClick={handleCharacterClick}
                  className="bg-dark-800 rounded-lg overflow-hidden hover:bg-dark-700 transition group cursor-pointer"
                >
                  {/* Character Image with hover preview and overlay text */}
                  <div className="relative aspect-[3/4] overflow-hidden group">
                    {/* Primary image */}
                    <img
                      src={character.avatar_url || '/avatars/default.png'}
                      alt={character.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = '/avatars/default.png';
                      }}
                    />
                    {/* Secondary image on hover (uses hover_url from backend if available) */}
                    {character.hover_url && character.hover_url !== character.avatar_url && (
                      <img
                        src={character.hover_url}
                        alt={character.name}
                        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-burgundy-900 bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center pointer-events-none">
                      <span className="text-white opacity-0 group-hover:opacity-100 font-semibold bg-burgundy-600 px-4 py-2 rounded">
                        {t.chat}
                      </span>
                    </div>
                    
                    {/* Character Info Overlay - positioned at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/60 to-transparent">
                      <div className="mb-2">
                        <h3 className="font-semibold text-lg text-white mb-1">
                          {character.name}
                          {character.age && character.age !== 'N/A' && (
                            <span className="text-sm text-white/80 ml-2">{character.age}</span>
                          )}
                        </h3>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-white/90 mb-3" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {description.substring(0, 100)}
                        {description.length > 100 ? '...' : ''}
                      </p>

                      {/* Engagement Stats */}
                      <div className="flex items-center gap-4 text-sm text-white/90">
                        <span className="flex items-center gap-1">🤍 {character.likes || 0}</span>
                        <span className="flex items-center gap-1">💬 {character.comments || '0'}</span>
                        <span className="flex items-center gap-1">📷 {character.images_count ?? 0}</span>
                        <span className="flex items-center gap-1">🎥 {character.videos_count ?? 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export default Explore;
